import { createElement, lazy, useEffect, useMemo, useState } from 'react';
import { EMPTY_DRAFTS, hasUnsavedNote, type NoteDrafts } from '../features/notesAndTags/noteDrafts';
import type { NotesAndTagsRecord } from '../features/notesAndTags/types';
import type { ContactPanelComposition, ContactPanelProps } from '../lib/contactPanels';
import { mq, useMediaQuery } from '../ui';
import { reportError } from './telemetry';

/**
 * Which feature's components fill which place on the contact page (F1-f spec FR-V2, FR-V3; F1-f WI-1.3
 * plan Decisions 18 and 19; the page replaced the contact pop-up, design-system §6, Rev 70). The
 * composition lives here, in the composition root, and `router.tsx` hands it to the contact page's route
 * as a prop, so `src/features/accounts` never imports
 * another feature and no feature imports `src/app` — `src/app/accountPanels.ts`'s arrangement.
 *
 * **`tags` and `notes` are filled with F1-e's sections** (F1-e spec FR-K2; F1-e WI-1.4 plan Decision
 * 30), each through a thin adapter below that takes `ContactPanelProps` and renders the section for
 * `{ entityType: 'contact', id }` at `h3`, under the view's title, with the place's `onRecordGone` — a
 * contact `404` from either section calls it once (F1-f spec FR-V4 (c)). The notes adapter also holds
 * the typed note text (`NoteDrafts`) above the section, passes `placement: 'dialog'` — a note delete is
 * confirmed inside the note, and the section holds no leave guard of its own (F1-f spec FR-V4 (a)) — and
 * reports `hasUnsavedNote(drafts)` through `onUnsavedChange`, which the page's one leave guard reads; that
 * guard lets a move between the contact's view and edit addresses through, since the page stays mounted
 * (F1-f spec FR-V4 (b)). Neither passes `onRecordChanged` on: a contact's tags and notes change nothing on the
 * contact, so the notes section takes no `onNoteWritten` for a contact (F1-e WI-1.4 plan Decision 14).
 *
 * Each section is loaded **as a `lazy()` import** over its named export, so it stays in its own chunk
 * and the contacts page's bundle budget does not grow with it (`pnpm check:bundle`); the loaders are
 * exported for their test. The order within a list is the order in the view; an unfilled place renders
 * nothing. The adapters are built with `createElement`, so this file stays `.ts`.
 *
 * A component's render error stays inside its place and is reported to the telemetry seam, never to the
 * console.
 */

/** The tags section's chunk, as a default export for `lazy()` — exported for its test (F1-e FR-K2). */
export const loadContactTagsSection = () =>
    import('../features/notesAndTags/RecordTagsSection').then((module) => ({
        default: module.RecordTagsSection,
    }));

const RecordTagsSectionLazy = lazy(loadContactTagsSection);

/** The notes section's chunk, as a default export for `lazy()` — exported for its test (F1-e FR-K2). */
export const loadContactNotesSection = () =>
    import('../features/notesAndTags/RecordNotesSection').then((module) => ({
        default: module.RecordNotesSection,
    }));

const RecordNotesSectionLazy = lazy(loadContactNotesSection);

/** The section's record for the view's contact, kept while the id is the same. */
function useContactRecord(contactId: number): NotesAndTagsRecord {
    return useMemo(() => ({ entityType: 'contact', id: contactId }), [contactId]);
}

/** The `tags` place: the contact's *Tags* section (F1-f spec FR-V3). */
export function ContactTagsPlace({ contactId, onRecordGone }: ContactPanelProps) {
    const record = useContactRecord(contactId);
    // Level 2: the contact page's sections sit directly under its `h1`.
    return createElement(RecordTagsSectionLazy, { record, headingLevel: 2, onRecordGone });
}

/**
 * The `notes` place: the contact's *Notes* section in the in-place (`dialog`) placement, with the typed
 * text held here and reported to the page as unsaved whenever it would be lost (F1-f spec FR-V3, FR-V4).
 */
export function ContactNotesPlace({ contactId, onRecordGone, onUnsavedChange }: ContactPanelProps) {
    const record = useContactRecord(contactId);
    const [drafts, setDrafts] = useState<NoteDrafts>(EMPTY_DRAFTS);
    // From `bp/laptop` the contact page fills the window, so the notes scroll inside their card.
    const bounded = useMediaQuery(mq.laptop);
    useEffect(() => {
        onUnsavedChange(hasUnsavedNote(drafts));
    }, [drafts, onUnsavedChange]);
    return createElement(RecordNotesSectionLazy, {
        record,
        headingLevel: 2,
        placement: 'dialog',
        bounded,
        drafts,
        onDraftsChange: setDrafts,
        onRecordGone,
    });
}

export const contactPanelComposition: ContactPanelComposition = {
    panels: {
        tags: [ContactTagsPlace],
        notes: [ContactNotesPlace],
    },
    reportPanelError: (error, place) => reportError({ error, where: `contact panel ${place}` }),
};
